import React, { useMemo } from 'react';
import { ContentFeatureModel, Layout } from '../../../../fetch-data/graphql';
import { splitBySpecifierHTML } from './utils';
import { useHydrationStatus } from '../../../../hooks/custom/use-hydration-status';
import {
  Row,
  Column,
  ContentContainer,
  MediaColumn,
  FlowRow,
  FlowMediaColumn,
  FlowColumn,
} from './styles';
import { renderAsHeading, renderMarkdown } from './overrides/markdown';
import { useViewport, ViewportType } from '../../../../hooks/use-viewport';
import { Breakpoints } from '@volkswagen-onehub/components-core';
import { MediaFeature } from '../MediaFeature/MediaFeature';
import { isValidMedia } from '../MediaFeature/helpers';

interface ContentFeatureProps {
  feature: ContentFeatureModel;
}

export const ContentFeature: React.FC<ContentFeatureProps> = ({ feature }) => {
  const {
    title,
    content,
    media,
    layout,
    layoutIndex: layoutNumber = 1,
    layoutColumnBreakSpecifier,
    layoutColumnBreakSpecifierIndex: layoutColumnBreakSpecifierNumber = 1,
    flowTextBelowMedia,
    centerMediaAroundLayout = false,
    centerTextAroundLayout = false,
  } = feature;

  const layoutIndex = Math.max(layoutNumber - 1, 0);
  const layoutColumnBreakSpecifierIndex = Math.max(
    layoutColumnBreakSpecifierNumber - 1,
    0
  );

  const viewport = useViewport(Breakpoints.b560, Breakpoints.b1280);

  const blocks = content
    .filter(({ html }) => Boolean(html))
    .map(({ html }) => html!);

  if (!blocks.length) return null;

  const before = blocks.slice(0, layoutIndex);
  const target = blocks[layoutIndex];
  const hasInternalBreak =
    !!layoutColumnBreakSpecifier && layoutColumnBreakSpecifier.trim() !== '';
  const next = !hasInternalBreak ? blocks[layoutIndex + 1] : undefined;

  // If there's an internal break, layout consumes 1 block, otherwise 2.
  const after = hasInternalBreak
    ? blocks.slice(layoutIndex + 1)
    : blocks.slice(layoutIndex + 2);

  const renderTitle = title && title.html;

  const normalizeBeforeBlock = Boolean(renderTitle && before.length > 0);
  const normalizeLayoutBlock = Boolean(renderTitle && before.length === 0);

  return (
    <>
      <ContentContainer>
        {renderTitle && (
          <Row>
            <Column>{renderAsHeading({ text: title.html!, level: 2 })}</Column>
          </Row>
        )}

        {before.map((b, idx) => (
          <Row key={`before-${idx}`}>
            <Column $normalize={normalizeBeforeBlock && idx === 0}>
              {renderMarkdown({ text: b })}
            </Column>
          </Row>
        ))}

        {layout === Layout.TWO_COLUMN ? (
          <TwoColumnLayout
            target={target}
            next={next}
            media={media}
            specifier={layoutColumnBreakSpecifier}
            specifierIndex={layoutColumnBreakSpecifierIndex}
            normalize={normalizeLayoutBlock}
            viewport={viewport}
          />
        ) : layout === Layout.MEDIA_LEFT || layout === Layout.MEDIA_RIGHT ? (
          flowTextBelowMedia ? (
            <MediaFlowLayout
              target={target}
              next={next}
              media={media}
              layout={layout}
              normalize={normalizeLayoutBlock}
              viewport={viewport}
            />
          ) : (
            <MediaBlockLayout
              target={target}
              next={next}
              media={media}
              layout={layout}
              specifier={layoutColumnBreakSpecifier}
              specifierIndex={layoutColumnBreakSpecifierIndex}
              normalize={normalizeLayoutBlock}
              viewport={viewport}
              centerMediaAroundLayout={centerMediaAroundLayout}
              centerTextAroundLayout={centerTextAroundLayout}
            />
          )
        ) : (
          <SingleColumnLayout
            target={target}
            next={next}
            media={media}
            normalize={normalizeLayoutBlock}
            viewport={viewport}
          />
        )}

        {after.map((b, idx) => (
          <Row key={`after-${idx}`}>
            <Column>{renderMarkdown({ text: b })}</Column>
          </Row>
        ))}
      </ContentContainer>
    </>
  );
};

interface SingleColumnLayoutProps {
  target: string;
  next?: string;
  media?: any[] | undefined;
  normalize?: boolean;
  viewport: ViewportType;
}

const SingleColumnLayout: React.FC<SingleColumnLayoutProps> = ({
  target,
  next,
  media,
  normalize = false,
}) => {
  const hasMedia = media && media.length > 0;

  return (
    <>
      {hasMedia && <MediaColumn>{renderContentMedia(media)}</MediaColumn>}
      <Row>
        <Column $normalize={normalize}>
          {renderMarkdown({ text: target })}
        </Column>
      </Row>

      {next && (
        <Row>
          <Column>{renderMarkdown({ text: next })}</Column>
        </Row>
      )}
    </>
  );
};

interface TwoColumnLayoutProps {
  target: string;
  next?: string;
  media?: any[] | undefined;
  specifier?: string;
  specifierIndex?: number;
  normalize?: boolean;
  viewport: ViewportType;
}

const TwoColumnLayout: React.FC<TwoColumnLayoutProps> = ({
  target,
  next,
  media,
  specifier = '',
  specifierIndex = 0,
  normalize = false,
}) => {
  const isHydrated = useHydrationStatus();
  const splitResult = useMemo(
    () => splitBySpecifierHTML(target, specifier, specifierIndex),
    [target, specifier, specifierIndex, isHydrated]
  );

  const { left, right, listStart } = splitResult;
  const hasMedia = media && media.length > 0;

  if (right) {
    // split inside same block
    return (
      <>
        {hasMedia && <MediaColumn>{renderContentMedia(media)}</MediaColumn>}
        <Row>
          <Column $normalize={normalize}>
            {renderMarkdown({ text: left })}
          </Column>
          <Column $normalize={normalize}>
            {renderMarkdown({ text: right, listStart })}
          </Column>
        </Row>
      </>
    );
  }

  // fallback: use two blocks
  return (
    <>
      {hasMedia && <MediaColumn>{renderContentMedia(media)}</MediaColumn>}
      <Row>
        <Column $normalize={normalize}>
          {renderMarkdown({ text: target })}
        </Column>
        {next && (
          <Column $normalize={normalize}>
            {renderMarkdown({ text: next })}
          </Column>
        )}
      </Row>
    </>
  );
};

interface MediaBlockLayoutProps {
  target: string;
  next?: string;
  media?: any[] | undefined;
  layout: Layout;
  specifier?: string;
  specifierIndex?: number;
  normalize?: boolean;
  viewport: ViewportType;
  centerMediaAroundLayout?: boolean | null;
  centerTextAroundLayout?: boolean | null;
}

const MediaBlockLayout: React.FC<MediaBlockLayoutProps> = ({
  target,
  next,
  media,
  layout,
  specifier = '',
  specifierIndex = 0,
  normalize = false,
  viewport,
  centerMediaAroundLayout = false,
  centerTextAroundLayout = false,
}) => {
  const reverse = layout === Layout.MEDIA_RIGHT;
  const isHydrated = useHydrationStatus();
  const splitResult = useMemo(
    () => splitBySpecifierHTML(target, specifier, specifierIndex),
    [target, specifier, specifierIndex, isHydrated]
  );

  const { left, right, listStart } = splitResult;

  const isMobile = viewport === ViewportType.MOBILE;
  const mediaColumnMaxWidth = getMediaColumnMaxWidth(media);

  if (right) {
    return (
      <>
        <Row $reverse={reverse} $centerTextVertically={centerTextAroundLayout}>
          <MediaColumn
            $isTwoColumn={true}
            $reverse={reverse}
            $centerVertically={centerMediaAroundLayout}
            $maxWidth={mediaColumnMaxWidth}
          >
            {renderContentMedia(media, true)}
          </MediaColumn>
          <Column
            $normalize={normalize && !isMobile}
            $centerVertically={centerTextAroundLayout}
          >
            {renderMarkdown({ text: left })}
          </Column>
        </Row>
        <Row>
          <Column>{renderMarkdown({ text: right, listStart })}</Column>
        </Row>
      </>
    );
  }

  return (
    <>
      <Row $reverse={reverse} $centerTextVertically={centerTextAroundLayout}>
        <MediaColumn
          $isTwoColumn={true}
          $reverse={reverse}
          $centerVertically={centerMediaAroundLayout}
          $maxWidth={mediaColumnMaxWidth}
        >
          {renderContentMedia(media, true)}
        </MediaColumn>
        <Column
          $normalize={normalize && !isMobile}
          $centerVertically={centerTextAroundLayout}
        >
          {renderMarkdown({ text: target })}
        </Column>
      </Row>
      {next && (
        <Row>
          <Column>{renderMarkdown({ text: next })}</Column>
        </Row>
      )}
    </>
  );
};

interface MediaFlowLayoutProps {
  target: string;
  next?: string;
  media?: any[] | undefined;
  layout: Layout;
  normalize?: boolean;
  viewport: ViewportType;
}

const MediaFlowLayout: React.FC<MediaFlowLayoutProps> = ({
  target,
  next,
  media,
  layout,
  normalize = false,
  viewport,
}) => {
  const reverse = layout === Layout.MEDIA_RIGHT;
  const isMobile = viewport === ViewportType.MOBILE;
  const mediaColumnMaxWidth = getMediaColumnMaxWidth(media);

  return (
    <>
      <FlowRow>
        <FlowMediaColumn
          $isTwoColumn={true}
          $reverse={reverse}
          $maxWidth={mediaColumnMaxWidth}
        >
          {renderContentMedia(media, true)}
        </FlowMediaColumn>
        <FlowColumn $normalize={normalize && !isMobile}>
          {renderMarkdown({ text: target })}
        </FlowColumn>
      </FlowRow>
      {next && (
        <Row>
          <Column>{renderMarkdown({ text: next })}</Column>
        </Row>
      )}
    </>
  );
};

export const renderContentMedia = (
  media: any[] | undefined,
  isTwoColumn = false
) => {
  if (!media) return null;

  const validMedia = media.filter((m) => isValidMedia(m));

  return validMedia.map((m, i) => (
    <MediaFeature key={i} feature={m} isTwoColumn={isTwoColumn} />
  ));
};

export const getMediaColumnMaxWidth = (
  media: any[] | undefined
): number | undefined => {
  if (!media) return undefined;

  const validMedia = media.filter((m) => isValidMedia(m));

  if (validMedia.length === 0) {
    return undefined;
  }

  // Check if ALL valid medias have forceMaxWidth
  const allHaveForceMaxWidth = validMedia.every(
    (m) => m?.forceMaxWidth != null && m.forceMaxWidth > 0
  );

  if (!allHaveForceMaxWidth) {
    return undefined;
  }

  // All medias have forceMaxWidth, use the largest one
  const forceMaxWidths = validMedia.map((m) => m.forceMaxWidth!);
  return Math.max(...forceMaxWidths);
};
